import React, { useState, useEffect } from 'react';
import { Card, Typography, Row, Col, message, Button, Modal, InputNumber } from 'antd';
import { ArrowLeftOutlined, EditOutlined, LoadingOutlined, HistoryOutlined } from '@ant-design/icons';
import axios from 'axios';
import ENVIRONMENT from '../../../../environments';
import AdminMainLayout from '../AdminMainLayout';
import CustomersTable from '../CustomersTable/CustomersTable';
import * as Utilities from '../../Utilities';

const { Title, Text } = Typography;

const getHistoryActionLabel = (item) => {
  const diff = parseInt(item.new_value) - parseInt(item.old_value);
  if (item.action_type === 'update_credits') return `Added ${diff} credits`;
  if (item.action_type === 'update_seats') return `Added ${diff} seats`;
  return item.action_type;
};

const CompanyDetails = () => {
  const [companyId, setCompanyId] = useState(null);
  const [companyData, setCompanyData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [totalUsers, setTotalUsers] = useState(0);
  const [editModalVisible, setEditModalVisible] = useState(false);
  const [seatsValue, setSeatsValue] = useState(null);
  const [creditsToAdd, setCreditsToAdd] = useState(0);
  const [saving, setSaving] = useState(false);
  const [seatsError, setSeatsError] = useState('');
  const [creditsError, setCreditsError] = useState('');
  const [historyModalVisible, setHistoryModalVisible] = useState(false);
  const [historyData, setHistoryData] = useState([]);
  const [historyLoading, setHistoryLoading] = useState(false);

  useEffect(() => {
    const pathParts = window.location.pathname.split('/');
    const idFromPath = pathParts[pathParts.length - 1];
    if (idFromPath && idFromPath !== 'CompanyDetails') {
      setCompanyId(idFromPath);
      fetchCompanyDetails(idFromPath);
    } else {
      message.error('Company ID not found');
      window.location.href = '/admin_customers';
    }
  }, []);

  const fetchCompanyDetails = async (id) => {
    try {
      setLoading(true);
      const response = await axios.post(ENVIRONMENT.COMPANY_GET_BATCH, {
        company_id: id
      });

      if (response.data && response.data.length > 0) {
        setCompanyData(response.data[0]);
      } else {
        message.error('Company not found');
        window.location.href = '/admin_customers';
      }
    } catch (error) {
      console.error('Error fetching company details:', error);
      message.error('Failed to fetch company details');
      window.location.href = '/admin_customers';
    } finally {
      setLoading(false);
    }
  };

  const openEditModal = () => {
    setSeatsValue(0);
    setCreditsToAdd(0);
    setSeatsError('');
    setCreditsError('');
    setEditModalVisible(true);
  };

  const openHistoryModal = async () => {
    setHistoryModalVisible(true);
    setHistoryLoading(true);
    try {
      const response = await axios.post(ENVIRONMENT.COMPANY_SETTINGS_CONTROLLER, {
        action: 'get_settings_update_history',
        company_id: parseInt(companyId),
      });
      setHistoryData(response.data || []);
    } catch (error) {
      console.error('Error fetching history:', error);
      message.error('Failed to fetch history');
    } finally {
      setHistoryLoading(false);
    }
  };

  const handleSave = async () => {
    const hasSeatsError = seatsValue < 0;
    const hasCreditsError = creditsToAdd < 0;
    setSeatsError(hasSeatsError ? 'Value must be 0 or greater' : '');
    setCreditsError(hasCreditsError ? 'Value must be 0 or greater' : '');
    if (hasSeatsError || hasCreditsError) return;

    try {
      setSaving(true);
      const payload = {
        action: 'update',
        company_id: parseInt(companyId),
        admin_username: localStorage.getItem('username'),
      };
      if (seatsValue > 0) {
        payload.add_seats = seatsValue;
      }
      if (creditsToAdd > 0) {
        payload.add_credits = creditsToAdd;
      }
      const response = await axios.post(ENVIRONMENT.COMPANY_SETTINGS_CONTROLLER, payload);
      if (response.data?.statusCode === 200) {
        message.success(response.data.message || 'Company settings updated successfully');
        setEditModalVisible(false);
        fetchCompanyDetails(companyId);
      } else {
        message.error(response.data?.message || 'Something went wrong');
      }
    } catch (error) {
      console.error('Error updating company settings:', error);
      message.error(error.response?.data?.message || 'Something went wrong');
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <AdminMainLayout selected="admin_customers">
        <div style={{ textAlign: 'center', padding: '50px' }}>
          <LoadingOutlined spin={true} style={{ fontSize: 36 }} />
          <div style={{ marginTop: '20px' }}>Loading company details...</div>
        </div>
      </AdminMainLayout>
    );
  }

  if (!companyData) {
    return (
      <AdminMainLayout selected="admin_customers">
        <div style={{ textAlign: 'center', padding: '50px' }}>
          <Text>Company not found</Text>
        </div>
      </AdminMainLayout>
    );
  }

  return (
    <AdminMainLayout selected="admin_customers">
      <div style={{ padding: '24px' }}>
        <Row gutter={[24, 24]}>
          <Col span={24}>
            <Button
              icon={<ArrowLeftOutlined />}
              onClick={() => window.location.href = '/admin_customers'}
              style={{ marginBottom: '16px' }}
            >
              Back to Companies
            </Button>
            <Title level={2}>Company Details</Title>
          </Col>

          <Col span={24}>
            <Card
              title="Basic Information"
              style={{ marginBottom: '24px' }}
              extra={
                Utilities.isSuperAdmin() ?
                <div style={{ display: 'flex', gap: '8px' }}>
                  <Button icon={<HistoryOutlined />} onClick={openHistoryModal}>
                    History
                  </Button>
                  <Button icon={<EditOutlined />} onClick={openEditModal}>
                    Edit
                  </Button>
                </div>: ''
              }
            >
              <Row gutter={[16, 16]} style={{marginTop: '8px'}}>
                <Col span={12}>
                  <Text strong style={{ paddingLeft: '32px' }}>Company ID:</Text>
                  <br />
                  <Text style={{ paddingLeft: '32px' }}>{companyData.company_id}</Text>
                </Col>
                <Col span={12}>
                  <Text strong>Company Name:</Text>
                  <br />
                  <Text>{companyData.company_display_name}</Text>
                </Col>
                <Col span={12}>
                  <Text strong style={{ paddingLeft: '32px' }}>Domain:</Text>
                  <br />
                  <Text style={{ paddingLeft: '32px' }}>{companyData.company_domain}</Text>
                </Col>
                <Col span={12}>
                  <Text strong>Subscription:</Text>
                  <br />
                  <Text>{companyData.subscription_data?.subscription_name || 'N/A'}</Text>
                </Col>
                <Col span={12}>
                  <Text strong style={{ paddingLeft: '32px' }}>Subscription Status:</Text>
                  <br />
                  <Text style={{ paddingLeft: '32px' }}>{companyData.subscription_data?.subscription_status || 'N/A'}</Text>
                </Col>
                <Col span={12}>
                  <Text strong>Seats:</Text>
                  <br />
                  <Text>{companyData.used_seats} used out of {companyData.no_of_seats}</Text>
                </Col>
                <Col span={12}>
                  <Text strong style={{ paddingLeft: '32px' }}>Credits:</Text>
                  <br />
                  <Text style={{ paddingLeft: '32px' }}>{companyData.subscription_data?.used_credits} used out of {companyData.subscription_data?.used_credits + companyData.subscription_data?.available_credits}</Text>
                </Col>
              </Row>
            </Card>
          </Col>

          {companyData && (
            <Col span={24}>
              <Card
                title={`Company Users (${totalUsers})`}
                style={{ marginBottom: '24px' }}
                extra={
                  <Text type="secondary">
                    Showing users for company: {companyData.company_display_name}
                  </Text>
                }
              >
                <CustomersTable
                  company_id={companyId}
                  onUserCountChange={setTotalUsers}
                />
              </Card>
            </Col>
          )}
        </Row>
      </div>

      {/* Edit Modal */}
      <Modal
        title={<span className="manrope f-18 black-00 w-600">Edit Company Settings</span>}
        visible={editModalVisible}
        onCancel={() => setEditModalVisible(false)}
        centered
        width={480}
        footer={[
          <div className="justify-in-end" key="edit_company">
            <Button
              className="modal-okay square font-14"
              disabled={saving || (!seatsValue && !creditsToAdd)}
              onClick={handleSave}
            >
              Save Changes {saving ? <LoadingOutlined spin /> : ''}
            </Button>
          </div>
        ]}
      >
        <p className="manrope f-14 w-500 mt-10 mb-5">Add Seats</p>
        <p className="manrope f-12 grey-77 mb-5">
          Currently: {companyData.used_seats} used out of {companyData.no_of_seats}
        </p>
        <InputNumber
          value={seatsValue}
          precision={0}
          parser={val => val.replace(/[^\d]/g, '')}
          onChange={val => { setSeatsValue(val); setSeatsError(''); }}
          className={`collab-access-form-fields${seatsError ? ' has-error-border' : ''}`}
          style={{ width: '100%' }}
        />
        {seatsError && <p className="manrope f-12" style={{ color: '#ff4d4f', marginTop: '4px' }}>{seatsError}</p>}

        <p className="manrope f-14 w-500 mt-20 mb-5">Add Credits</p>
        <p className="manrope f-12 grey-77 mb-5">
          Current balance: {companyData.subscription_data?.available_credits} credits
        </p>
        <InputNumber
          value={creditsToAdd}
          precision={0}
          parser={val => val.replace(/[^\d]/g, '')}
          onChange={val => { setCreditsToAdd(val); setCreditsError(''); }}
          className={`collab-access-form-fields${creditsError ? ' has-error-border' : ''}`}
          style={{ width: '100%' }}
        />
        {creditsError && <p className="manrope f-12" style={{ color: '#ff4d4f', marginTop: '4px' }}>{creditsError}</p>}
      </Modal>

      {/* History Modal */}
      <Modal
        visible={historyModalVisible}
        onCancel={() => setHistoryModalVisible(false)}
        maskClosable={true}
        footer={false}
        width={800}
        closeIcon={
          <img
            src="/img/closeIcon.png"
            alt="Close Modal Icon"
            style={{ marginTop: 28, marginRight: 20, cursor: 'pointer', borderRadius: '4px' }}
            onClick={() => setHistoryModalVisible(false)}
          />
        }
      >
        <Row style={{ marginBottom: 16 }}>
          <Col span={24} style={{ fontSize: 24, color: '#141414' }}>
            Update History
          </Col>
        </Row>

        <div className="rejection-modal" style={{ height: '60vh', overflowY: 'scroll' }}>
          {historyLoading ? (
            <div style={{ textAlign: 'center', padding: '50px' }}>
              <LoadingOutlined spin={true} style={{ fontSize: 36 }} />
            </div>
          ) : historyData.length === 0 ? (
            <div style={{ textAlign: 'center', padding: '50px', color: '#888' }}>
              <span className="manrope f-14">No history found.</span>
            </div>
          ) : (
            historyData.map((item) => (
              <div key={item.id} style={{ margin: 5 }}>
                <div style={{ marginTop: 25 }}>
                  <div className="note-bg-artist">
                    Date <span style={{ color: '#276DD7', paddingLeft: 5 }}>{Utilities.convertUtcToLocalTimezone(item.created_at) || '-'}</span>
                    <span style={{ paddingLeft: 25 }}>Action</span>
                    <span style={{ color: '#276DD7', paddingLeft: 5 }}>{getHistoryActionLabel(item)}</span>
                  </div>
                </div>
                <div style={{ display: 'flex', paddingTop: 10, paddingLeft: 4, gap: 40 }}>
                  <div>Admin: <span style={{ color: '#333333', paddingLeft: 5 }}>{item.admin_username}</span></div>
                  <div>Previous Value: <span style={{ color: '#333333', paddingLeft: 5 }}>{item.old_value}</span></div>
                  <div>New Value: <span style={{ color: '#333333', paddingLeft: 5 }}>{item.new_value}</span></div>
                </div>
              </div>
            ))
          )}
        </div>
      </Modal>
    </AdminMainLayout>
  );
};

export default CompanyDetails;
